import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { OrderModel } from '@merchant-clients/order';

import { StatusIndicator } from '@frontend/components';
import { useLocale } from '@frontend/hooks';
import { tCommon, tOrder } from '@frontend/locales';
import {
  deliveryOptionTypeMap,
  OrderCardSizeVariantType,
  statusTypeMap,
  usePersistentUiSettings,
} from '@frontend/utils';

import { InfoRow } from './info-row.component';
import { sizeSettingsMap } from './order-list-item.component';

export interface OrderInfoProps {
  order: OrderModel;
  variant: 'neutral' | 'warning' | 'success' | 'error';
  displayChildPlace?: boolean;
  hideTimeParameters?: boolean;
  /** Override the card size from display settings. Pass a fixed value to prevent the detail view from inheriting the user's card size preference. */
  cardSize?: OrderCardSizeVariantType;
}

const valueSx = {
  minWidth: 0,
  textAlign: 'right' as const,
  overflowWrap: 'anywhere' as const,
};

const pad = (num: number) => String(num).padStart(2, '0');

export const OrderInfo = ({
  order,
  variant,
  displayChildPlace = false,
  hideTimeParameters,
  cardSize: cardSizeProp,
}: OrderInfoProps) => {
  const theme = useTheme();
  const { formatters } = useLocale();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const {
    ordersPage: { orderCardSettings },
  } = usePersistentUiSettings();

  const cardSize = cardSizeProp ?? orderCardSettings.cardSize;
  const fontSize = sizeSettingsMap[cardSize].card.fontSize;

  // biome-ignore lint/correctness/useExhaustiveDependencies: the original deps are intentional, adding timeRequested changes when the timer stops
  useEffect(() => {
    if (order.status !== 'PENDING' || order.timeRequested) {
      return;
    }

    const interval = setInterval(() => {
      const now = new Date();
      const diff = Math.floor(
        (now.getTime() - new Date(order.createdAt).getTime()) / 1000,
      );
      setElapsedSeconds(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [order.status, order.createdAt]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the original [elapsedSeconds] is intentional, it keeps the last waiting time after the order leaves PENDING
  const formattedWaitingTime = useMemo(() => {
    if (order.status !== 'PENDING' || order.timeRequested) {
      return;
    }

    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `${pad(minutes)}:${pad(seconds)}`;
  }, [elapsedSeconds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the original [order.timeRequested] is intentional, adding formatters changes when it recomputes
  const formattedPickupTime = useMemo(() => {
    if (!order.timeRequested) {
      return;
    }

    const requested = new Date(order.timeRequested);
    return formatters.relativeDatetime(requested);
  }, [order.timeRequested]);

  return (
    <Stack spacing={0.75}>
      {/* Place row — verification number + place name(s) */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Typography variant="h5" noWrap sx={{ flexShrink: 0, pr: 1.5 }}>
          #{order.verification}
        </Typography>
        {displayChildPlace && order.placeNames.length > 1 && (
          <Stack direction="column" alignItems="flex-end" sx={{ minWidth: 0 }}>
            {order.placeNames.map((place, index) => (
              <Typography
                key={`${place}-${index}`}
                variant="body2"
                color={index === 0 ? 'textPrimary' : 'textSecondary'}
                fontSize={fontSize}
                sx={valueSx}
              >
                {place}
              </Typography>
            ))}
          </Stack>
        )}
        {(!displayChildPlace || order.placeNames.length === 1) && (
          <Typography variant="body2" fontSize={fontSize} sx={valueSx}>
            {order.placeNames[0] ||
              deliveryOptionTypeMap.get(order.deliveryOption)!()}
          </Typography>
        )}
      </Stack>

      {/* Status row — indicator is inline so it always stays adjacent to the text */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={0.75}
      >
        <Typography
          variant="body2"
          noWrap
          fontSize={fontSize}
          sx={{ flexShrink: 0 }}
        >
          {tCommon.general.status()}:
        </Typography>
        {/*
         * Inline text flow: indicator and text are part of the same inline
         * context so the indicator always stays on the first line adjacent to
         * the text, even when the status string wraps to a second line.
         *
         * The indicator span has height = one line-height and uses
         * alignItems:center so the bar is vertically centered on the text,
         * identical to the original flex+calc approach.
         */}
        <Box sx={valueSx}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              height: `calc(${fontSize} * ${theme.typography.body2.lineHeight ?? 1.43})`,
              verticalAlign: 'top',
              mr: 0.75,
            }}
          >
            <StatusIndicator variant={variant} />
          </Box>
          <Typography
            component="span"
            variant="body2"
            fontSize={fontSize}
            color={
              variant !== 'neutral' ? theme.palette[variant].dark : undefined
            }
          >
            {statusTypeMap.get(order.status)!()}
          </Typography>
        </Box>
      </Stack>

      {!hideTimeParameters && (
        <>
          <Divider />

          <InfoRow label={tCommon.general.received()} fontSize={fontSize}>
            <Typography variant="body2" fontSize={fontSize} sx={valueSx}>
              {formatters.relativeDatetime(new Date(order.createdAt))}
            </Typography>
          </InfoRow>

          {formattedPickupTime && (
            <InfoRow label={tOrder.orderList.pickUp()} fontSize={fontSize}>
              <Typography variant="body2" fontSize={fontSize} sx={valueSx}>
                {formattedPickupTime}
              </Typography>
            </InfoRow>
          )}

          {formattedWaitingTime && (
            <InfoRow label={tOrder.orderList.waitTime()} fontSize={fontSize}>
              <Typography variant="body2" fontSize={fontSize} sx={valueSx}>
                {formattedWaitingTime}
              </Typography>
            </InfoRow>
          )}
        </>
      )}
    </Stack>
  );
};
